import { RISK_GROUPS, RISK_KEY_SUBSTITUTIONS, RiskKeys } from '@/config';
import getSignalTranslation from '@/helpers/getSignalTranslation';
import getUsdAmount from '@/helpers/getUsdAmount';
import type { RiskType } from '@/models';

export type ConnectionsRiskFilterCategory = 'all' | RiskType;

export interface ConnectionsPanelFilter {
  search: string;
  riskCategory: ConnectionsRiskFilterCategory;
}

const RISK_CATEGORY_ORDER: RiskType[] = ['danger', 'suspicious', 'trusted'];

const CONNECTIONS_FILTER_RISK_LABEL_KEY: Record<RiskType, string> = {
  danger: 'translation.connections_filter_risk_high',
  suspicious: 'translation.connections_filter_risk_suspicious',
  trusted: 'translation.connections_filter_risk_trusted',
};

export const CONNECTIONS_FILTER_RISK_OPTIONS: readonly {
  value: ConnectionsRiskFilterCategory;
  translationKey: string;
}[] = [
  { value: 'all', translationKey: 'translation.all' },
  ...RISK_CATEGORY_ORDER.map((risk) => ({
    value: risk,
    translationKey: CONNECTIONS_FILTER_RISK_LABEL_KEY[risk],
  })),
];

function entityTypeToRiskKey(type: string | undefined): RiskKeys | null {
  if (!type || typeof type !== 'string') return null;
  const normalized = type.trim().toLowerCase();
  if (normalized === 'entity') return null;

  const substituted = RISK_KEY_SUBSTITUTIONS[normalized];
  if (substituted != null) return substituted;

  const knownKeys = new Set<string>(Object.values(RiskKeys));
  if (knownKeys.has(normalized)) {
    return normalized as RiskKeys;
  }

  return null;
}

function riskKeyToCategory(key: RiskKeys): RiskType | null {
  for (const category of RISK_CATEGORY_ORDER) {
    if (RISK_GROUPS[category].includes(key)) {
      return category;
    }
  }
  return null;
}

export function getEntityTypeSvgIconName(
  entityType: string | undefined,
): string | null {
  const riskKey = entityTypeToRiskKey(entityType);
  if (riskKey == null) return null;
  const category = riskKeyToCategory(riskKey);
  if (category == null) return null;
  return `risk-${category}`;
}

export interface ConnectionEntity {
  id?: string;
  name?: string;
  slug?: string;
  type?: string;
}

export interface ConnectionTotals {
  total?: number;
  hops?: number;
  direct?: number;
}

export interface CounterpartyConnectionItem {
  id?: string;
  entity?: ConnectionEntity;
  received?: ConnectionTotals;
  sent?: ConnectionTotals;
}

function formatUsdIntegerDollars(whole: number): string {
  const sign = whole < 0 ? '-' : '';
  const digits = Math.abs(Math.trunc(whole)).toString();
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `$${sign}${grouped}`;
}

export function formatConnectionUsd(
  raw: number | undefined,
  divisor?: number,
  options?: { omitFractionIfWhole?: boolean },
): string {
  if (raw == null || Number.isNaN(Number(raw))) {
    return '—';
  }
  const value = Number(raw);
  const denom = divisor != null && divisor > 0 ? divisor : 100;
  const amount = value / denom;
  if (options?.omitFractionIfWhole) {
    const cents = Math.round(amount * 100);
    if (cents % 100 === 0) {
      return formatUsdIntegerDollars(cents / 100);
    }
  }
  return getUsdAmount(amount);
}

export function formatConnectionHopsValue(
  hops: number | undefined | null,
): string {
  if (hops == null || Number.isNaN(Number(hops))) {
    return '—';
  }
  return String(hops);
}

export function humanizeEntityType(type: string | undefined): string {
  if (!type) return '';
  return type.split('_').join(' ');
}

export function getCounterpartyEntityTypeLabel(
  entityType: string | undefined,
): string {
  if (!entityType) return '';
  const riskKey = entityTypeToRiskKey(entityType);
  if (riskKey != null) {
    return getSignalTranslation(riskKey);
  }
  return humanizeEntityType(entityType);
}

export function filterNamedConnections(
  connections: CounterpartyConnectionItem[] | null | undefined,
): CounterpartyConnectionItem[] {
  if (!connections || !Array.isArray(connections)) return [];

  return connections.filter((connection) => {
    return !!connection.entity?.name || !!connection.entity?.slug;
  });
}

function hasPositiveDirectVolume(
  totals: ConnectionTotals | undefined,
): boolean {
  const direct = totals?.direct;
  return typeof direct === 'number' && direct > 0;
}

export type ConnectionsSegmentMode = 'all' | 'direct';

export const CONNECTIONS_SEGMENT_OPTIONS: readonly {
  mode: ConnectionsSegmentMode;
  translationKey: string;
}[] = [
  { mode: 'all', translationKey: 'translation.all' },
  { mode: 'direct', translationKey: 'translation.direct' },
];

export function filterConnectionsBySegment(
  list: CounterpartyConnectionItem[],
  mode: ConnectionsSegmentMode,
): CounterpartyConnectionItem[] {
  if (mode === 'all') return list;
  return list.filter(
    (connection) =>
      hasPositiveDirectVolume(connection.received) ||
      hasPositiveDirectVolume(connection.sent),
  );
}

export function getConnectionEntityRiskCategory(
  entityType: string | undefined,
): RiskType | null {
  const riskKey = entityTypeToRiskKey(entityType);
  if (riskKey == null) return null;
  return riskKeyToCategory(riskKey);
}

function connectionMatchesSearchQuery(
  connection: CounterpartyConnectionItem,
  queryLower: string,
): boolean {
  if (queryLower.length === 0) return true;
  const name = (connection.entity?.name ?? '').toLowerCase();
  const slug = (connection.entity?.slug ?? '').toLowerCase();
  const typeLine = getCounterpartyEntityTypeLabel(
    connection.entity?.type,
  ).toLowerCase();
  return (
    name.includes(queryLower) ||
    slug.includes(queryLower) ||
    typeLine.includes(queryLower)
  );
}

export function filterConnectionsByPanel(
  list: CounterpartyConnectionItem[],
  panel: ConnectionsPanelFilter,
): CounterpartyConnectionItem[] {
  const queryLower = panel.search.trim().toLowerCase();
  const { riskCategory } = panel;

  return list.filter((connection) => {
    if (riskCategory !== 'all') {
      const rowCategory = getConnectionEntityRiskCategory(
        connection.entity?.type,
      );
      if (rowCategory !== riskCategory) return false;
    }
    return connectionMatchesSearchQuery(connection, queryLower);
  });
}

export function applyCounterpartyConnectionsFilters(
  named: CounterpartyConnectionItem[],
  segment: ConnectionsSegmentMode,
  panel: ConnectionsPanelFilter,
): CounterpartyConnectionItem[] {
  return filterConnectionsByPanel(
    filterConnectionsBySegment(named, segment),
    panel,
  );
}

export function getCounterpartyConnectionRowKey(
  connection: CounterpartyConnectionItem,
  index: number,
): string {
  return (
    connection.id ??
    connection.entity?.id ??
    `${connection.entity?.slug ?? connection.entity?.name ?? 'row'}-${index}`
  );
}
